import * as React from 'react';
import { MouseEventHandler } from 'react';
import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CircleStackIcon,
  CreditCardIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Badge, Button, Heading, IconButton } from '@restworld/ui-ds-base';
import { cn, useResponsiveBreakPoints } from '@restworld/utils-common';

export interface EmployerInvoiceCardProps {
  invoiceNumber?: number;
  companyName?: string;
  issueDate?: string;
  slots?: number;
  discount?: number;
  amount?: number;
  status?: 'to_be_paid' | 'paid' | 'installment';
  statusText?: string;
  onDownloadInvoice?: MouseEventHandler<HTMLButtonElement>;
  isAnnualSubscription?: boolean;
  downloadInvoicePending?: boolean;
  discountType?: 'percentage' | 'fixed';
  hostedInvoiceUrl?: string;
  onPayInvoice?: () => void;
  isPpu?: boolean;
}

export function EmployerInvoiceCard({
  invoiceNumber,
  companyName,
  issueDate,
  slots,
  discount,
  amount,
  status,
  statusText,
  onDownloadInvoice,
  isAnnualSubscription,
  discountType,
  downloadInvoicePending,
  hostedInvoiceUrl,
  onPayInvoice,
  isPpu,
}: EmployerInvoiceCardProps) {
  const { t } = useTranslation('common');
  const { t: tPayment } = useTranslation('payment-and-invoices');

  const cardActions = (
    <>
      {!!statusText && (
        <StatusBadge variant={status === 'paid' ? 'success' : status === 'to_be_paid' ? 'danger' : 'warning'}>
          {statusText}
        </StatusBadge>
      )}
      {!!onDownloadInvoice && (
        <IconButton
          onClick={onDownloadInvoice}
          Icon={ArrowDownTrayIcon}
          variant="neutral"
          ariaLabel="download-invoice"
          loading={downloadInvoicePending}
        />
      )}
      {!!hostedInvoiceUrl && !!onPayInvoice && (
        <Button variant="secondary-danger" StartIcon={CreditCardIcon} onClick={onPayInvoice}>
          {tPayment('pay')}
        </Button>
      )}
    </>
  );
  return (
    <CardContainer>
      <CardContent>
        <CardActions className="lg:hidden">{cardActions}</CardActions>
        <InvoiceNumber invoiceNumber={invoiceNumber} />
        {/* Real Tailwind variants, not twin.macro's lg:(...) group syntax, which is a dead class under
            className/Turbopack — without it the row never becomes flex at lg, so the status/download/pay
            actions dropped to a second line left-aligned instead of sitting at the row's right edge. */}
        <div className="lg:flex lg:items-center lg:justify-between lg:w-full">
          <InfoItemsContainer>
            {!!companyName && <InfoItem icon={BuildingOfficeIcon} text={companyName} />}
            {!!issueDate && <InfoItem icon={CalendarIcon} text={issueDate} />}
            {!!slots && (
              <InfoItem
                icon={CircleStackIcon}
                text={
                  isPpu
                    ? `${slots} ${slots > 1 ? t('days_key') : t('day_key')}`
                    : `${slots} ${slots > 1 ? 'slots' : 'slot'}`
                }
                isAnnualSubscription={isAnnualSubscription}
              />
            )}
            {!!discount && (
              <InfoItem
                icon={FaceSmileIcon}
                text={`${t('discount')}: ${discount}${discountType === 'percentage' ? '%' : '€'}`}
              />
            )}
            <InfoItem icon={BanknotesIcon} text={`${amount}€`} />
          </InfoItemsContainer>
          <CardActions className="hidden lg:flex">{cardActions}</CardActions>
        </div>
      </CardContent>
    </CardContainer>
  );
}

const InfoItem = ({
  icon: Icon,
  text,
  isAnnualSubscription,
}: {
  icon: React.ElementType;
  text?: string;
  isAnnualSubscription?: boolean;
}) => {
  const { t } = useTranslation('common');
  return (
    <InfoItemContainer>
      <IconWrapper>
        <Icon />
      </IconWrapper>
      <InfoText>{isAnnualSubscription ? t('annual_subscription') : text}</InfoText>
    </InfoItemContainer>
  );
};

const CardContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'w-full px-6 py-5 rounded-2xl border border-neutral-40 shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)] justify-start items-center gap-6 inline-flex',
      className
    )}
    {...props}
  />
);
const CardContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('grow shrink basis-0 flex-col justify-start items-start gap-2 lg:gap-0 inline-flex', className)}
    {...props}
  />
);
const InvoiceNumber = ({ invoiceNumber }: { invoiceNumber?: number }) => {
  const { isMobile, isTablet } = useResponsiveBreakPoints();
  return (
    <div className="self-stretch">
      <Heading size={5} weight="semibold" color="neutral" maxChars={isMobile ? 25 : isTablet ? 50 : 100}>
        {invoiceNumber?.toString()}
      </Heading>
    </div>
  );
};
const InfoItemsContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('self-stretch justify-start items-center gap-y-2 gap-x-5 inline-flex flex-wrap', className)}
    {...props}
  />
);
const CardActions = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full flex justify-between items-center lg:justify-end lg:gap-3 lg:w-fit', className)}
    {...props}
  />
);
const StatusBadge = ({ className, ...props }: any) => (
  <Badge className={cn('px-2 py-0.5 rounded self-start md:self-center', className)} {...props} />
);
const InfoItemContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('justify-start items-center gap-0.5 flex', className)} {...props} />
);
const IconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-4 h-4 relative', className)} {...props} />
);
const InfoText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('text-neutral-200 text-sm font-medium', className)} {...props} />
);

export default EmployerInvoiceCard;
